import { useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';

import { UserRole } from 'context';
import { useStudentGroupTeacherContactsQuery } from 'core/graphql';
import { authState } from 'core/state';

import { transformTeacherContacts } from './transform';
import type { ITeacherContact } from './transform';

interface IUseGroupTeacherContacts {
  contacts: ITeacherContact[];
  loading: boolean;
  error: string;
}

/**
 * #58 «Napisz do prowadzącego»: преподаватели группы, которым студент может
 * написать в Slack группы (только Slack, без почты — решение владельца 07.10).
 *
 * 🔴 Запрос уходит ТОЛЬКО под активной ролью Student. Карточка курса
 * рендерится и преподавателю, а `myGroupTeacherContacts` сверяет активную роль
 * в токене и преподавателю отвечает ошибкой прав. Гейт здесь, а не в
 * компоненте: хук нельзя вызвать «не под той ролью» по забывчивости.
 *
 * Ошибка (в т.ч. `LMS_ACCESS_SUSPENDED` — её разводит глобальный errorLink)
 * даёт пустой список: кнопки просто нет, страница курса не падает.
 */
export const useGroupTeacherContacts = (groupId?: string | null): IUseGroupTeacherContacts => {
  const { active_role: activeRole } = useReactiveVar(authState);
  const skip = activeRole !== UserRole.STUDENT || !groupId;

  const { data, loading, error } = useStudentGroupTeacherContactsQuery({
    variables: { groupId: groupId || '' },
    skip,
    // Преподавателя привязывают в Slack из ERP — следующий заход на страницу
    // курса должен это увидеть без перезагрузки.
    fetchPolicy: 'cache-and-network',
  });

  const contacts = useMemo(
    () => (skip || error ? [] : transformTeacherContacts(data)),
    [skip, error, data],
  );

  return {
    contacts,
    loading: !skip && loading && !data,
    error: skip ? '' : error?.message || '',
  };
};
